import React, { useRef } from "react";
import { Text } from "styles/utility-components";

import { DropdownMenu, DropdownMenuItem, DropdownMenuSeperator } from "components/ui/dropdown_menu";
import Tooltip from "components/ui/tooltip/Tooltip";
import { SuggestedQuestion } from "../types";

// Long prompts without a name are cut off at this width rather than stretching the menu
const MENU_MAX_WIDTH = 480;

type Props = {
  trigger: React.ReactNode;
  // Each shows its title, or the prompt itself when it has none
  prompts: SuggestedQuestion[];
  onSelectPrompt: (quickPrompt: SuggestedQuestion) => void;
  // Puts focus in the chat's input as the menu closes, instead of back on the trigger
  focusInput: () => void;
  // Why there are no prompts to offer, e.g. they failed to load; shown over the disabled trigger
  unavailableReason?: string;
  // Adds an "Edit quick prompts" item below the prompts
  onEditPrompts?: () => void;
  side?: "top" | "bottom";
  align?: "start" | "center" | "end";
};

/**
 * Quick prompts menu for an AI chat. Choosing a prompt hands it to
 * `onSelectPrompt` instead of sending it, so the user can review or edit it first.
 */
export default function QuickPromptsMenu({
  trigger,
  prompts,
  onSelectPrompt,
  focusInput,
  unavailableReason,
  onEditPrompts,
  side,
  align,
}: Props) {
  const isEditingRef = useRef(false);

  if (unavailableReason) {
    return (
      <Tooltip content={unavailableReason}>
        <span>{trigger}</span>
      </Tooltip>
    );
  }

  return (
    <DropdownMenu
      width="fit-content"
      maxWidth={MENU_MAX_WIDTH}
      side={side}
      align={align}
      trigger={trigger}
      onCloseAutoFocus={(event) => {
        event.preventDefault();
        focusInput();
        if (isEditingRef.current) {
          isEditingRef.current = false;
          // Opened once the menu has gone, so the drawer returns focus to the input on close
          onEditPrompts?.();
        }
      }}
    >
      {prompts.map((quickPrompt) => (
        <DropdownMenuItem
          key={quickPrompt.id ?? quickPrompt.prompt}
          onClick={() => onSelectPrompt(quickPrompt)}
        >
          {quickPrompt.title ?? quickPrompt.prompt}
        </DropdownMenuItem>
      ))}
      {onEditPrompts && (
        <>
          <DropdownMenuSeperator />
          <DropdownMenuItem
            icon="edit05"
            iconProps={{ color: "sysTextSecondary" }}
            onClick={() => {
              isEditingRef.current = true;
            }}
          >
            <Text color="sysTextSecondary">Edit quick prompts</Text>
          </DropdownMenuItem>
        </>
      )}
    </DropdownMenu>
  );
}
